import { SimulationConfig, Ball } from '../types';
import { PerformanceTelemetry } from '../services/PerformanceTelemetry';

// WGSL Source code for ambient drift simulation
const AMBIENT_PARTICLES_WGSL = `
struct Particle {
    pos: vec2<f32>,
    vel: vec2<f32>,
    color: vec3<f32>,
    size: f32,
};

struct ComputeConfig {
    width: f32,
    height: f32,
    deltaTime: f32,
    particleCount: f32,
};

@group(0) @binding(0) var<storage, read> inputParticles: array<Particle>;
@group(0) @binding(1) var<storage, read_write> outputParticles: array<Particle>;
@group(0) @binding(2) var<uniform> config: ComputeConfig;

@compute @workgroup_size(64)
fn cs_main(@builtin(global_invocation_id) gid: vec3<u32>) {
    let index = gid.x;
    if (index >= u32(config.particleCount)) {
        return;
    }

    var p = inputParticles[index];
    
    // Update local translation by velocity with delta-scaling
    p.pos += p.vel * config.deltaTime;

    // Simple screen wrapping with boundary tolerances
    let half_w = config.width * 0.5;
    let half_h = config.height * 0.5;
    let boundary_x = half_w + 120.0;
    let boundary_y = half_h + 120.0;

    if (p.pos.x < -boundary_x) {
        p.pos.x = boundary_x;
    } else if (p.pos.x > boundary_x) {
        p.pos.x = -boundary_x;
    }

    if (p.pos.y < -boundary_y) {
        p.pos.y = boundary_y;
    } else if (p.pos.y > boundary_y) {
        p.pos.y = -boundary_y;
    }

    outputParticles[index] = p;
}
`;

/**
 * Task 3.1: Create compute shader pipeline abstractions and compile routines.
 */
export class WebGPUComputePipeline {
  private device: any;
  public pipeline: any = null;
  public bindGroupLayout: any = null;

  constructor(device: any, wgslSource: string) {
    this.device = device;
    this.compile(wgslSource);
  }

  private compile(wgslSource: string) {
    try {
      const shaderModule = this.device.createShaderModule({
        label: 'Compute Shader Module',
        code: wgslSource
      });

      // Bind group layout configuration matching inputs, outputs and uniform buffers
      this.bindGroupLayout = this.device.createBindGroupLayout({
        label: 'Compute Bind Group Layout',
        entries: [
          {
            binding: 0,
            visibility: 4, // GPUShaderStage.COMPUTE
            buffer: { type: 'read-only-storage' }
          },
          {
            binding: 1,
            visibility: 4, // GPUShaderStage.COMPUTE
            buffer: { type: 'storage' }
          },
          {
            binding: 2,
            visibility: 4, // GPUShaderStage.COMPUTE
            buffer: { type: 'uniform' }
          }
        ]
      });

      const pipelineLayout = this.device.createPipelineLayout({
        label: 'Compute Pipeline Layout',
        bindGroupLayouts: [this.bindGroupLayout]
      });

      this.pipeline = this.device.createComputePipeline({
        label: 'Compute Pipeline',
        layout: pipelineLayout,
        compute: {
          module: shaderModule,
          entryPoint: 'cs_main'
        }
      });
    } catch (e) {
      console.error('Failed to compile WebGPU Compute Pipeline:', e);
      throw e;
    }
  }
}

/**
 * Task 3.4: Program double-buffered ping-pong resources for state updates.
 */
export class WebGPUDoubleBuffer {
  private device: any;
  private bufferA: any;
  private bufferB: any;
  private currentSourceIsA = true;
  public byteSize: number;

  constructor(device: any, byteSize: number, usage: number, labelPrefix = 'DoubleBuffer') {
    this.device = device;
    this.byteSize = byteSize;

    this.bufferA = device.createBuffer({
      label: `${labelPrefix} Buffer A`,
      size: byteSize,
      usage: usage
    });

    this.bufferB = device.createBuffer({
      label: `${labelPrefix} Buffer B`,
      size: byteSize,
      usage: usage
    });
  }

  public getSourceBuffer() {
    return this.currentSourceIsA ? this.bufferA : this.bufferB;
  }

  public getDestinationBuffer() {
    return this.currentSourceIsA ? this.bufferB : this.bufferA;
  }

  public swap() {
    this.currentSourceIsA = !this.currentSourceIsA;
  }

  public writeInitialData(data: Float32Array) {
    this.device.queue.writeBuffer(this.bufferA, 0, data);
    this.device.queue.writeBuffer(this.bufferB, 0, data);
  }

  public destroy() {
    if (this.bufferA) this.bufferA.destroy();
    if (this.bufferB) this.bufferB.destroy();
  }
}

/**
 * Task 3.2: Build a Compute Pass Scheduler to organize execute sequences safely.
 */
export class WebGPUComputePassScheduler {
  private device: any;
  private scheduledPasses: Array<() => void> = [];

  constructor(device: any) {
    this.device = device;
  }

  public registerPass(dispatchFn: () => void) {
    this.scheduledPasses.push(dispatchFn);
  }

  public clearSchedule() {
    this.scheduledPasses = [];
  }

  public executeAll() {
    // Run scheduled functions sequentially within execution memory limits
    for (const runPass of this.scheduledPasses) {
      try {
        runPass();
      } catch (e) {
        console.error('Error executing compute scheduled pass:', e);
      }
    }
  }
}

/**
 * Combined WebGPU Manager carrying out background computations
 */
export class WebGPUComputeManager {
  private device: any;
  private pipeline: WebGPUComputePipeline;
  private scheduler: WebGPUComputePassScheduler;
  private particleBuffer: WebGPUDoubleBuffer;
  private configUniformBuffer: any;

  private particleCount = 256;
  private particleByteSize = 32; // vec2(8) + vec2(8) + vec3(12) + size(4) = 32 bytes Layout-Mapped
  private initialParticlesData: Float32Array;
  private readbackBuffer: any = null;

  private isReadPending = false;
  private lastVerifiedPos = { x: 0, y: 0 };

  constructor(device: any) {
    this.device = device;
    this.scheduler = new WebGPUComputePassScheduler(device);
    this.pipeline = new WebGPUComputePipeline(device, AMBIENT_PARTICLES_WGSL);

    // Set up uniform config buffer (4 floats = 16 bytes aligned)
    this.configUniformBuffer = device.createBuffer({
      label: 'Compute Configuration Buffer',
      size: 16,
      usage: 0x0040 | 0x0008  // UNIFORM | COPY_DST
    });

    // Create Ping-Pong double-buffered resources (SSBOs)
    const usage = 0x0080 | 0x0008 | 0x0004 | 0x0020; // STORAGE | COPY_DST | COPY_SRC | VERTEX
    this.particleBuffer = new WebGPUDoubleBuffer(
      device,
      this.particleCount * this.particleByteSize,
      usage,
      'AmbientParticles'
    );

    // Map and fill initial particle states
    this.initialParticlesData = this.generateInitialParticlesData();
    this.particleBuffer.writeInitialData(this.initialParticlesData);

    // Prepare readback buffer to verify compute pipeline output asynchronously (Task 3.5)
    this.readbackBuffer = device.createBuffer({
      label: 'Compute Diagnostics Readback',
      size: this.particleByteSize * 4, // Track the first 4 particles
      usage: 0x0008 | 0x0001 // COPY_DST | MAP_READ
    });

    this.setupSchedule();
  }

  private generateInitialParticlesData(): Float32Array {
    const data = new Float32Array(this.particleCount * 8); // 8 floats per particle
    for (let i = 0; i < this.particleCount; i++) {
      const idx = i * 8;
      // Coordinates matching core game bounds
      data[idx] = (Math.random() - 0.5) * 1200; // pos.x
      data[idx + 1] = (Math.random() - 0.5) * 800; // pos.y
      
      // Velocities
      data[idx + 2] = (Math.random() - 0.5) * 100; // vel.x
      data[idx + 3] = (Math.random() - 0.5) * 100; // vel.y

      // Colors
      data[idx + 4] = Math.random(); // r
      data[idx + 5] = Math.random(); // g
      data[idx + 6] = Math.random(); // b

      // Size
      data[idx + 7] = 2.0 + Math.random() * 4.0; // size
    }
    return data;
  }

  private setupSchedule() {
    this.scheduler.registerPass(() => {
      // Direct pass implementation inside register sequence
      const source = this.particleBuffer.getSourceBuffer();
      const dest = this.particleBuffer.getDestinationBuffer();

      const computeBindGroup = this.device.createBindGroup({
        label: 'Ambient Compute Pass BindGroup',
        layout: this.pipeline.bindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: source } },
          { binding: 1, resource: { buffer: dest } },
          { binding: 2, resource: { buffer: this.configUniformBuffer } }
        ]
      });

      const commandEncoder = this.device.createCommandEncoder({
        label: 'Compute Commands Encoder'
      });

      const passEncoder = commandEncoder.beginComputePass({
        label: 'Ambient Drift Pass'
      });
      passEncoder.setPipeline(this.pipeline.pipeline);
      passEncoder.setBindGroup(0, computeBindGroup);
      
      const workgroupCount = Math.ceil(this.particleCount / 64);
      passEncoder.dispatchWorkgroups(workgroupCount);
      passEncoder.end();

      // Schedule a readback once in a while to confirm correct calculations (Task 3.5)
      if (!this.isReadPending) {
        commandEncoder.copyBufferToBuffer(
          dest,
          0,
          this.readbackBuffer,
          0,
          this.particleByteSize * 4
        );
        this.isReadPending = true;
      }

      this.device.queue.submit([commandEncoder.finish()]);

      // Swap the input and output buffer states
      this.particleBuffer.swap();

      // Trigger asynchronous verify callback
      if (this.isReadPending) {
        this.readBufferResultsAsync();
      }
    });
  }

  private readBufferResultsAsync() {
    this.readbackBuffer.mapAsync(1).then(() => { // GPUMapMode.READ (0x1)
      const mappedRange = this.readbackBuffer.getMappedRange();
      const floatData = new Float32Array(mappedRange);
      
      // Update our diagnostics verification cache
      if (floatData.length >= 2) {
        this.lastVerifiedPos.x = floatData[0];
        this.lastVerifiedPos.y = floatData[1];
        
        // Report telemetry stats
        PerformanceTelemetry.bytesUploaded += this.particleByteSize * 4;
      }
      
      this.readbackBuffer.unmap();
      this.isReadPending = false;
    }).catch((e: any) => {
      // Reset flag to avoid hanging
      this.isReadPending = false;
    });
  }

  public update(width: number, height: number, dtSeconds: number) {
    if (!this.device) return;

    // Build configuration struct array (16 bytes)
    const configData = new Float32Array(4);
    configData[0] = width || 1920;
    configData[1] = height || 1080;
    configData[2] = dtSeconds || 0.016;
    configData[3] = this.particleCount;

    this.device.queue.writeBuffer(this.configUniformBuffer, 0, configData);

    // Invoke scheduler to execute computer passes on raw buffers
    this.scheduler.executeAll();
    
    // Accumulate metrics on memory, passes and allocations
    PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
    PerformanceTelemetry.vramDetails.storageBuffers += this.particleCount * this.particleByteSize * 2;
    PerformanceTelemetry.vramDetails.uniformBuffers += 16;
  }

  public getLastVerifiedCoordinate() {
    return this.lastVerifiedPos;
  }

  public getParticleDataBuffer() {
    // Current source buffer containing newly computed particle instance variables
    return this.particleBuffer.getSourceBuffer();
  }

  public getParticleCount() {
    return this.particleCount;
  }

  public dispose() {
    if (this.particleBuffer) this.particleBuffer.destroy();
    if (this.configUniformBuffer) this.configUniformBuffer.destroy();
    if (this.readbackBuffer) this.readbackBuffer.destroy();
  }
}

// ==========================================
// Phase 4 - Hybrid GPU Simulation Subsystem
// ==========================================

const BALL_COMPUTE_WGSL = `
struct BallState {
    pos_x: f32,
    pos_y: f32,
    vel_x: f32,
    vel_y: f32,
    radius: f32,
    mass: f32,
    r: f32,
    g: f32,
    b: f32,
    angle: f32,
    isBox: f32,
    spawnTime: f32,
};

struct RenderInstance {
    pos_x: f32,
    pos_y: f32,
    r: f32,
    g: f32,
    b: f32,
    radius: f32,
    padding1: f32,
    padding2: f32,
};

struct BallComputeConfig {
    width: f32,
    height: f32,
    deltaTime: f32,
    ballCount: f32,
    gravity: f32,
    airResistance: f32,
    containerRadius: f32,
    vortexStrength: f32,
    time: f32,
    camera_x: f32,
    camera_y: f32,
    camera_scale: f32,
    aggressiveLOD: f32,
    padding1: f32,
    padding2: f32,
    padding3: f32,
};

struct IndirectDrawArgs {
    vertex_count: u32,
    instance_count: atomic<u32>,
    first_vertex: u32,
    first_instance: u32,
};

@group(0) @binding(0) var<storage, read> inputBalls: array<BallState>;
@group(0) @binding(1) var<storage, read_write> outputBalls: array<BallState>;
@group(0) @binding(2) var<storage, read_write> renderInstances: array<RenderInstance>;
@group(0) @binding(3) var<uniform> config: BallComputeConfig;
@group(0) @binding(4) var<storage, read_write> drawArgs: array<IndirectDrawArgs, 3>;

@compute @workgroup_size(64)
fn cs_main(@builtin(global_invocation_id) gid: vec3<u32>) {
    let index = gid.x;
    if (index >= u32(config.ballCount)) {
        return;
    }

    var b = inputBalls[index];
    
    if (b.radius <= 0.0) {
        outputBalls[index] = b;
        return;
    }

    // Step 4.2 - Gravity and Basic Euler motion integrations
    let isBox = b.isBox > 0.5;
    if (!isBox) {
        b.vel_y += config.gravity * config.deltaTime;
    }

    let airRes = pow(config.airResistance, config.deltaTime);
    b.vel_x *= airRes;
    b.vel_y *= airRes;

    // Step 4.3 - Procedural vortex force and attraction fields
    if (config.vortexStrength > 0.0 && !isBox) {
        let dist = length(vec2<f32>(b.pos_x, b.pos_y));
        if (dist > 1.0) {
            let tx = -b.pos_y / dist;
            let ty = b.pos_x / dist;
            let force = config.vortexStrength * 120.0;
            b.vel_x += tx * force * config.deltaTime;
            b.vel_y += ty * force * config.deltaTime;
        }
    }

    b.pos_x += b.vel_x * config.deltaTime;
    b.pos_y += b.vel_y * config.deltaTime;
    b.angle += length(vec2<f32>(b.vel_x, b.vel_y)) * 0.002 * config.deltaTime;

    // Pulse radii & shift hues procedurally in step with global clock
    let age = config.time - b.spawnTime * 0.001;
    var displayRadius = b.radius;
    if (!isBox) {
        let pulse = sin(age * 4.0 + f32(index) * 0.1) * 0.06 + 1.0;
        displayRadius = b.radius * pulse;
        
        // Procedural hue cycle
        let hue_shift = sin(age * 1.5 + f32(index) * 0.2) * 0.08;
        b.r = clamp(b.r + hue_shift, 0.0, 1.0);
        b.g = clamp(b.g - hue_shift * 0.4, 0.0, 1.0);
        b.b = clamp(b.b + hue_shift * 0.4, 0.0, 1.0);
    }

    outputBalls[index] = b;

    // Task 7.2 & 7.3: In-Shader Frustum & Viewport Culling & Instance Stream Filtering
    let camera_scale = config.camera_scale;
    let camera_x = config.camera_x;
    let camera_y = config.camera_y;
    
    let screen_center_x = b.pos_x * camera_scale + camera_x;
    let screen_center_y = b.pos_y * camera_scale + camera_y;
    
    // safety margin around the screen limits (width/height)
    let scaled_radius = displayRadius * camera_scale;
    let margin = scaled_radius + 400.0; // wider margin to be safe for any rotations or extreme bounces
    
    let is_visible = (screen_center_x >= -margin && screen_center_x <= config.width + margin) &&
                     (screen_center_y >= -margin && screen_center_y <= config.height + margin);

    if (is_visible) {
        // Calculate dynamic LOD segment index based on size/telemetry (Task 9.2)
        let lod_score = displayRadius * camera_scale;
        var lod_index = 0u; // LOD 0: High Detail (Close/Large)
        if (config.aggressiveLOD > 0.5) {
            lod_index = 2u; // Force LOD 2 (Low Detail) to shed GPU rendering instruction pressure
        } else if (lod_score < 14.0) {
            lod_index = 2u; // LOD 2: Low Detail (Tiny/Far)
        } else if (lod_score < 32.0) {
            lod_index = 1u; // LOD 1: Medium Detail
        }

        let out_idx = atomicAdd(&drawArgs[lod_index].instance_count, 1u);
        
        var ri: RenderInstance;
        ri.pos_x = b.pos_x;
        ri.pos_y = b.pos_y;
        ri.r = b.r;
        ri.g = b.g;
        ri.b = b.b;
        ri.radius = displayRadius;
        ri.padding1 = f32(lod_index); // Store selected LOD as metadata
        ri.padding2 = 0.0;
        
        let max_capacity = u32(config.ballCount);
        let dst_idx = lod_index * max_capacity + out_idx;
        renderInstances[dst_idx] = ri;
    }
}
`;

export class WebGPUBallComputeSubsystem {
  private device: any;
  private pipeline: any = null;
  private bindGroupLayout: any = null;
  private inputBuffer: any = null;
  private outputBuffer: any = null;
  private renderInstancesBuffer: any = null;
  private configBuffer: any = null;
  private readbackBuffer: any = null;
  private indirectBuffer: any = null;
  private isReadPending = false;
  private capacity = 0;

  private readonly strideFloatCount = 12; // [x, y, vx, vy, radius, mass, r, g, b, angle, isBox, spawnTime]
  private readonly renderStrideFloatCount = 8; // RenderInstance

  constructor(device: any) {
    this.device = device;
    this.compilePipeline();
    // Start with a basic uniform config buffer (16 floats * 4 bytes = 64 bytes aligned to 16)
    this.configBuffer = device.createBuffer({
      label: 'Compute Ball Uniform Config Buffer',
      size: 64,
      usage: 0x0040 | 0x0008  // UNIFORM | COPY_DST
    });
  }

  private compilePipeline() {
    try {
      const shaderModule = this.device.createShaderModule({
        label: 'Ball Physics Compute Shader Module',
        code: BALL_COMPUTE_WGSL
      });

      this.bindGroupLayout = this.device.createBindGroupLayout({
        label: 'Ball Compute Bind Group Layout',
        entries: [
          { binding: 0, visibility: 4, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: 4, buffer: { type: 'storage' } },
          { binding: 2, visibility: 4, buffer: { type: 'storage' } },
          { binding: 3, visibility: 4, buffer: { type: 'uniform' } },
          { binding: 4, visibility: 4, buffer: { type: 'storage' } }
        ]
      });

      this.pipeline = this.device.createComputePipeline({
        label: 'Ball Physics Compute Pipeline',
        layout: this.device.createPipelineLayout({
          bindGroupLayouts: [this.bindGroupLayout]
        }),
        compute: {
          module: shaderModule,
          entryPoint: 'cs_main'
        }
      });
    } catch (e) {
      console.error('Failed to compile WebGPU Ball Compute Pipeline:', e);
    }
  }

  private allocateBuffers(requiredCount: number) {
    if (this.capacity >= requiredCount) return;

    const newCapacity = Math.max(requiredCount, Math.ceil(this.capacity * 1.5), 128);

    if (this.inputBuffer) this.inputBuffer.destroy();
    if (this.outputBuffer) this.outputBuffer.destroy();
    if (this.renderInstancesBuffer) this.renderInstancesBuffer.destroy();
    if (this.readbackBuffer) this.readbackBuffer.destroy();
    if (this.indirectBuffer) this.indirectBuffer.destroy();

    const ballsByteSize = newCapacity * this.strideFloatCount * 4;
    // Task 9.2: Sized up to 3 times to allocate separate contiguous slices for LOD 0, 1, and 2
    const renderByteSize = newCapacity * 3 * this.renderStrideFloatCount * 4;

    this.inputBuffer = this.device.createBuffer({
      label: 'Compute Ball Input Buffer',
      size: ballsByteSize,
      usage: 0x0080 | 0x0008 // STORAGE | COPY_DST
    });

    this.outputBuffer = this.device.createBuffer({
      label: 'Compute Ball Output Buffer',
      size: ballsByteSize,
      usage: 0x0080 | 0x0004 | 0x0008 // STORAGE | COPY_SRC | COPY_DST
    });

    this.renderInstancesBuffer = this.device.createBuffer({
      label: 'Compute Ball Render Instances Buffer',
      size: renderByteSize,
      usage: 0x0080 | 0x0020 | 0x0008 // STORAGE | VERTEX | COPY_DST
    });

    this.readbackBuffer = this.device.createBuffer({
      label: 'Compute Ball Readback Buffer',
      size: ballsByteSize,
      usage: 0x0008 | 0x0001 // COPY_DST | MAP_READ
    });

    // Allocate GPU-driven indirect draw arguments buffer for 3 LOD slots (3 * 16 = 48 bytes)
    const indirectUsage = (typeof (globalThis as any).GPUBufferUsage !== 'undefined'
      ? (globalThis as any).GPUBufferUsage.INDIRECT | (globalThis as any).GPUBufferUsage.COPY_DST | (globalThis as any).GPUBufferUsage.STORAGE
      : 0x0100 | 0x0008 | 0x0080);
    this.indirectBuffer = this.device.createBuffer({
      label: 'Compute Ball Indirect Draw Buffer',
      size: 48,
      usage: indirectUsage
    });

    this.capacity = newCapacity;
  }

  public updateAndSimulate(balls: Ball[], config: SimulationConfig, dtSeconds: number, worldTimeSec: number, camera: any, zoomLevel = 1.0, canvasWidth = 1920, canvasHeight = 1080) {
    if (!this.device || !this.pipeline || balls.length === 0) return;

    this.allocateBuffers(balls.length);

    // Formulate inputs from CPU ball states
    const cpuInput = new Float32Array(balls.length * this.strideFloatCount);
    for (let i = 0; i < balls.length; i++) {
       const b = balls[i];
       const offset = i * this.strideFloatCount;
       cpuInput[offset + 0] = b.pos.x;
       cpuInput[offset + 1] = b.pos.y;
       cpuInput[offset + 2] = b.vel.x;
       cpuInput[offset + 3] = b.vel.y;
       cpuInput[offset + 4] = b.radius;
       cpuInput[offset + 5] = b.mass;

       // Color unpacking
       const hex = b.color || '#ffffff';
       cpuInput[offset + 6] = parseInt(hex.slice(1, 3), 16) / 255 || 1.0;
       cpuInput[offset + 7] = parseInt(hex.slice(3, 5), 16) / 255 || 1.0;
       cpuInput[offset + 8] = parseInt(hex.slice(5, 7), 16) / 255 || 1.0;

       cpuInput[offset + 9] = b.angle || 0;
       cpuInput[offset + 10] = b.isBox ? 1 : 0;
       cpuInput[offset + 11] = b.spawnTime || 0;
    }

    // Write input coordinates to the GPU buffer
    this.device.queue.writeBuffer(this.inputBuffer!, 0, cpuInput);

    // Formulate uniform configuration (64 bytes aligned to 16)
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const finalScaleValue = camera.scale * zoomLevel * dpr;

    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const finalTx = camera.x * finalScaleValue + centerX;
    const finalTy = camera.y * finalScaleValue + centerY;

    const configData = new Float32Array(16);
    configData[0] = canvasWidth;
    configData[1] = canvasHeight;
    configData[2] = dtSeconds;
    configData[3] = balls.length;
    configData[4] = config.gravity;
    configData[5] = config.airResistance || 1.0;
    configData[6] = config.containerRadius || 400;
    configData[7] = (config as any).vortexStrength || ((config as any).enableSpaceSpiral ? 2.5 : 0.0);
    configData[8] = worldTimeSec;
    configData[9] = finalTx;
    configData[10] = finalTy;
    configData[11] = finalScaleValue;
    configData[12] = (config as any).aggressiveLOD ? 1.0 : 0.0;
    configData[13] = 0.0;
    configData[14] = 0.0;
    configData[15] = 0.0;

    this.device.queue.writeBuffer(this.configBuffer!, 0, configData);

    // Overwrite the entire 48 bytes of the 3 LOD argument slots with resetting parameters
    const initialArgs = new Uint32Array([
      6, 0, 0, 0,                       // Slot 0 (LOD 0: High): starts at instance index 0
      6, 0, 0, balls.length,            // Slot 1 (LOD 1: Medium): starts at instance index balls.length
      6, 0, 0, balls.length * 2         // Slot 2 (LOD 2: Low): starts at instance index balls.length * 2
    ]);
    this.device.queue.writeBuffer(this.indirectBuffer!, 0, initialArgs);

    // Execute Pass commands
    const commandEncoder = this.device.createCommandEncoder({ label: 'Ball Compute Pass' });
    const bindGroup = this.device.createBindGroup({
      label: 'Ball Compute BindGroup',
      layout: this.bindGroupLayout!,
      entries: [
        { binding: 0, resource: { buffer: this.inputBuffer! } },
        { binding: 1, resource: { buffer: this.outputBuffer! } },
        { binding: 2, resource: { buffer: this.renderInstancesBuffer! } },
        { binding: 3, resource: { buffer: this.configBuffer! } },
        { binding: 4, resource: { buffer: this.indirectBuffer! } }
      ]
    });

    const passEncoder = commandEncoder.beginComputePass({ label: 'Ball Motion Simulation with View Culling' });
    passEncoder.setPipeline(this.pipeline);
    passEncoder.setBindGroup(0, bindGroup);
    
    const workgroupCount = Math.ceil(balls.length / 64);
    passEncoder.dispatchWorkgroups(workgroupCount);
    passEncoder.end();

    this.device.queue.submit([commandEncoder.finish()]);

    // Gather telemetry
    PerformanceTelemetry.vramDetails.storageBuffers += balls.length * (this.strideFloatCount * 2 + this.renderStrideFloatCount) * 4;
    PerformanceTelemetry.vramDetails.uniformBuffers += 48;
    PerformanceTelemetry.bytesUploaded += cpuInput.byteLength + configData.byteLength + 16;
  }

  public getRenderInstancesBuffer(): any {
    return this.renderInstancesBuffer;
  }

  public getIndirectBuffer(): any {
    return this.indirectBuffer;
  }

  public readbackDataToBalls(balls: Ball[]) {
    // Prevent overlapping mappings
    if (this.isReadPending || !this.readbackBuffer || !this.outputBuffer || balls.length === 0) return;

    this.isReadPending = true;

    const byteSize = balls.length * this.strideFloatCount * 4;
    const commandEncoder = this.device.createCommandEncoder({ label: 'Readback copy command' });
    commandEncoder.copyBufferToBuffer(this.outputBuffer, 0, this.readbackBuffer, 0, byteSize);
    this.device.queue.submit([commandEncoder.finish()]);

    this.readbackBuffer.mapAsync(1).then(() => { // GPUMapMode.READ = 1
      const arrayBuffer = this.readbackBuffer!.getMappedRange(0, byteSize);
      const floatData = new Float32Array(arrayBuffer);

      const count = Math.min(balls.length, floatData.length / this.strideFloatCount);
      for (let i = 0; i < count; i++) {
        const offset = i * this.strideFloatCount;
        const b = balls[i];
        if (b) {
          // Task 4.4: Main thread synchronizes with GPU transforms and syncs to worker thread on next tick
          b.pos.x = floatData[offset + 0];
          b.pos.y = floatData[offset + 1];
          b.vel.x = floatData[offset + 2];
          b.vel.y = floatData[offset + 3];
          b.angle = floatData[offset + 9];
        }
      }

      this.readbackBuffer!.unmap();
      this.isReadPending = false;
    }).catch((e: any) => {
      console.warn('WebGPU Ball readback coordinate mapping rejected:', e);
      this.isReadPending = false;
    });
  }

  public dispose() {
    if (this.inputBuffer) this.inputBuffer.destroy();
    if (this.outputBuffer) this.outputBuffer.destroy();
    if (this.renderInstancesBuffer) this.renderInstancesBuffer.destroy();
    if (this.configBuffer) this.configBuffer.destroy();
    if (this.readbackBuffer) this.readbackBuffer.destroy();
    if (this.indirectBuffer) this.indirectBuffer.destroy();
  }
}
